import React, { useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from "axios";
import { Navigate } from "react-router-dom";
import { Routes } from "constants/routes"
import { API_BASE_URL, API_URLS, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";
import { isTokenValid } from "utils/token";
import UserStore from "stories/UserStore";
import {useNavigate} from "react-router";

interface Props {
  children: React.ReactNode;
}

export const ProtectedRoute = observer(({ children }: Props) => {
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);
  const navigate = useNavigate();
  const isLoggingOut = (window as any).__isLoggingOut;

  useEffect(() => {
    (async () => {
      try {
        const userResponse = await axios.get(`${API_BASE_URL}${API_URLS.User}`, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          timeout: 20000,
        });

        let userData: any = userResponse.data?.data ?? {};

        // Дополнительно подтягиваем расширенный профиль (avatar_url, display_name и т.п.),
        // чтобы при входе сразу показывать актуальный аватар и никнейм в сайдбаре.
        try {
          const profileResponse = await axios.get(`${API_BASE_URL}/profile`, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
            timeout: 20000,
          });

          const profile = profileResponse.data?.data ?? {};
          userData = {
            ...userData,
            // берём то, что есть в профиле, но не затираем уже имеющиеся значения
            display_name:
              typeof profile.display_name === "string" && profile.display_name.trim()
                ? profile.display_name
                : userData.display_name,
            avatar_url:
              typeof profile.avatar_url !== "undefined" ? profile.avatar_url : userData.avatar_url,
            phone_number:
              typeof profile.phone_number === "string" && profile.phone_number.trim()
                ? profile.phone_number
                : userData.phone_number,
            country_id:
              typeof profile.country_id !== "undefined" ? profile.country_id : userData.country_id,
          };
        } catch (profileError) {
          console.warn("[ProtectedRoute] Failed to load extended profile, using base user only", profileError);
        }

        UserStore.setUser(userData);
      } catch (error: any) {
        const status = error?.response?.status;
        const message = error?.response?.data?.message;
        const isTimeout = error?.code === "ECONNABORTED";

        console.error("[ProtectedRoute] Failed to load user", {
          status,
          message,
          isTimeout,
        });

        if (
          status === 401 ||
          status === 403 ||
          status === 404 ||
          message === "Unauthenticated." ||
          isTimeout
        ) {
          localStorage.removeItem(LOCALSTORAGE_TOKEN_ITEM);
          UserStore.clearUser();
          navigate(Routes.SignIn);
        }
      }
    })();
  }, []);

  if (!token) {
    if (isLoggingOut) {
      return null;
    }
    return <Navigate to={Routes.SignIn} replace />;
  }

  if (!UserStore.user) {
    return (
      <Container>
        <ProgressSpinner />
      </Container>
    );
  }

  return children;
});

const Container = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
